import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Input, InputNumber, Popconfirm, Select, Skeleton, Space,
  Statistic, Table, Tag, Typography, message,
} from 'antd';
import { DateField, TextField } from '../../../ui/fields';
import { Field, FormModal, FormSection } from '../../../ui/forms';
import { PlusOutlined } from '@ant-design/icons';
import type {
  CreateHomeworkRequest, HomeworkRow, HomeworkStatus, HomeworkSubmissionRow, HomeworkSubmissionStatus,
} from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin surface — Homework.
 * List across all sections; open a homework to see its submissions table.
 * Teachers use the same routes filtered by `mine`.
 */
const STATUS_TONE: Record<HomeworkStatus, string> = {
  draft: 'default', published: 'processing', closed: 'success',
};
const SUB_TONE: Record<HomeworkSubmissionStatus, string> = {
  submitted: 'processing', graded: 'success', returned: 'warning', missing: 'error',
};

export function HomeworkPage() {
  const { institution = '', code } = useParams<{ institution: string; code?: string }>();
  if (code) return <HomeworkDetail institution={institution} id={code} />;
  return <HomeworkList institution={institution} />;
}

function HomeworkList({ institution }: { institution: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const list = useQuery({
    queryKey: ['homework', institution],
    queryFn: () => productApi.listHomework(institution),
    enabled: !!institution,
  });
  const rows = list.data?.rows ?? [];
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>Homework</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            Every assignment across every section. Click a row to see submissions.
          </Typography.Text>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>Assign</Button>
      </div>

      <Card size="small" title={`${rows.length} assignments`}>
        {list.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}
        {rows.length === 0 && !list.isLoading && (
          <Empty description={<span>Nothing assigned yet. <a onClick={() => setAddOpen(true)}>Assign the first</a>.</span>} style={{ padding: 20 }} />
        )}
        {rows.length > 0 && (
          <Table<HomeworkRow>
            size="small"
            pagination={{ pageSize: 15 }}
            rowKey="id"
            dataSource={rows}
            onRow={(r) => ({ onClick: () => navigate(`/i/${institution}/admin/homework/${r.id}`), style: { cursor: 'pointer' } })}
            columns={[
              { title: 'Title', dataIndex: 'title', key: 't', render: (v: string) => <b>{v}</b> },
              { title: 'Subject', dataIndex: 'subject_name', key: 's' },
              { title: 'Section', key: 'sec', width: 100, render: (_, r) => `${r.grade}${r.section ? `-${r.section}` : ''}` },
              { title: 'Teacher', dataIndex: 'teacher_name', key: 'te', render: (v: string | null) => v ?? <Typography.Text type="secondary">unassigned</Typography.Text> },
              { title: 'Due', dataIndex: 'due_on', key: 'd', width: 110, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Submissions', key: 'sub', width: 130, align: 'right', render: (_, r) => <span>{r.submissions_count} / {r.students_count}</span> },
              { title: 'Status', dataIndex: 'status', key: 'st', width: 100, render: (v: HomeworkStatus) => <Tag color={STATUS_TONE[v]}>{v}</Tag> },
            ]}
          />
        )}
      </Card>

      <NewHomeworkModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        institution={institution}
        onCreated={(id) => {
          setAddOpen(false);
          void qc.invalidateQueries({ queryKey: ['homework', institution] });
          void message.success('Homework assigned');
          navigate(`/i/${institution}/admin/homework/${id}`);
        }}
      />
    </Space>
  );
}

function HomeworkDetail({ institution, id }: { institution: string; id: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [gradeFor, setGradeFor] = useState<HomeworkSubmissionRow | null>(null);
  const q = useQuery({
    queryKey: ['homework', institution, id],
    queryFn: () => productApi.getHomework(institution, id),
    enabled: !!institution && !!id,
  });
  const closeMut = useMutation({
    mutationFn: (status: HomeworkStatus) => productApi.updateHomeworkStatus(institution, id, { status }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['homework', institution, id] }); },
  });

  if (q.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!q.data) return <Alert type="error" showIcon message="Not found" />;
  const hw = q.data.homework;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space size={4}>
        <Button type="text" size="small" onClick={() => navigate(`/i/${institution}/admin/homework`)}>← Homework</Button>
      </Space>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>{hw.title}</Typography.Title>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {hw.subject_name} · {hw.grade}{hw.section ? `-${hw.section}` : ''} · due <b>{hw.due_on}</b>
          </Typography.Text>
        </div>
        <Space>
          <Tag color={STATUS_TONE[hw.status]}>{hw.status}</Tag>
          {hw.status === 'published' && (
            <Popconfirm title="Close this assignment?" onConfirm={() => closeMut.mutate('closed')} okText="Close">
              <Button>Close</Button>
            </Popconfirm>
          )}
        </Space>
      </div>
      {hw.description && <Card size="small"><Typography.Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{hw.description}</Typography.Paragraph></Card>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="Students" value={hw.students_count} /></Card>
        <Card size="small"><Statistic title="Submitted" value={q.data.submissions.filter((s) => s.status === 'submitted' || s.status === 'graded').length} valueStyle={{ color: '#1c6442' }} /></Card>
        <Card size="small"><Statistic title="Graded" value={q.data.submissions.filter((s) => s.status === 'graded').length} valueStyle={{ color: '#553a9c' }} /></Card>
        <Card size="small"><Statistic title="Missing" value={q.data.submissions.filter((s) => s.status === 'missing').length} valueStyle={{ color: '#b3382f' }} /></Card>
      </div>

      <Card size="small" title="Submissions">
        <Table<HomeworkSubmissionRow>
          size="small"
          pagination={{ pageSize: 20 }}
          rowKey="student_id"
          dataSource={q.data.submissions}
          columns={[
            { title: 'Adm. no.', dataIndex: 'admission_number', key: 'a', width: 110, render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
            { title: 'Name', dataIndex: 'student_name', key: 'n' },
            { title: 'Status', dataIndex: 'status', key: 's', width: 120, render: (v: HomeworkSubmissionStatus) => <Tag color={SUB_TONE[v]}>{v}</Tag> },
            { title: 'Grade', dataIndex: 'grade_out_of_10', key: 'g', width: 90, align: 'right', render: (v: number | null) => v == null ? <Typography.Text type="secondary">—</Typography.Text> : <b>{v}/10</b> },
            { title: 'Note', dataIndex: 'note', key: 'no', ellipsis: true, render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
            {
              key: 'act', width: 120, align: 'right',
              render: (_, r) => <Button size="small" onClick={() => setGradeFor(r)}>Grade</Button>,
            },
          ]}
        />
      </Card>

      <GradeModal
        target={gradeFor}
        institution={institution}
        homeworkId={id}
        onClose={() => setGradeFor(null)}
        onDone={() => {
          setGradeFor(null);
          void qc.invalidateQueries({ queryKey: ['homework', institution, id] });
          void message.success('Saved');
        }}
      />
    </Space>
  );
}

type NewHomeworkFormValues = { subject_id: string; grade: string; title: string; description?: string; due_on: string };

function NewHomeworkModal({
  open, onClose, institution, onCreated,
}: { open: boolean; onClose: () => void; institution: string; onCreated: (id: string) => void }) {
  const students = useQuery({
    queryKey: ['students-search', institution, ''],
    queryFn: () => productApi.listStudents(institution),
    enabled: !!institution && open,
  });
  const subjects = useQuery({
    queryKey: ['subjects', institution],
    queryFn: () => productApi.listSubjects(institution),
    enabled: !!institution && open,
  });
  const sections = new Map<string, { grade: string; section: string }>();
  for (const s of students.data?.rows ?? []) sections.set(`${s.grade}::${s.section}`, { grade: s.grade, section: s.section });
  const today = new Date().toISOString().slice(0, 10);

  return (
    <FormModal<NewHomeworkFormValues, { id: string }>
      open={open} onClose={onClose}
      title="Assign homework" submitText="Assign"
      initial={{ due_on: today }}
      mutation={(v) => {
        const [grade, section] = v.grade.split('::');
        return productApi.createHomework(institution, {
          subject_id: v.subject_id,
          grade: grade ?? '', section: section ?? '',
          title: v.title,
          description: v.description?.trim() || undefined,
          due_on: v.due_on,
        } satisfies CreateHomeworkRequest);
      }}
      onSuccess={(r) => onCreated(r.id)}
    >
      <FormSection cols={2}>
        <Field name="subject_id" label="Subject" required>
          <Select options={(subjects.data?.rows ?? []).map((s) => ({ value: s.id, label: `${s.name} (${s.code})` }))} />
        </Field>
        <Field name="grade" label="Section" required>
          <Select options={Array.from(sections.values()).map((s) => ({ value: `${s.grade}::${s.section}`, label: `${s.grade}${s.section ? `-${s.section}` : ''}` }))} />
        </Field>
      </FormSection>
      <Field name="title" label="Title" required>
        <TextField placeholder="Chapter 5 questions 1–8" autoFocus />
      </Field>
      <Field name="description" label="Description">
        <Input.TextArea rows={4} />
      </Field>
      <Field name="due_on" label="Due on" required>
        <DateField />
      </Field>
    </FormModal>
  );
}

function GradeModal({
  target, institution, homeworkId, onClose, onDone,
}: {
  target: HomeworkSubmissionRow | null; institution: string; homeworkId: string;
  onClose: () => void; onDone: () => void;
}) {
  if (!target) return null;
  return (
    <FormModal<{ grade: number; status: HomeworkSubmissionStatus; feedback?: string }>
      open={!!target} onClose={onClose}
      title={`Grade · ${target.student_name}`}
      submitText="Save"
      initial={{ grade: target.grade_out_of_10 ?? 0, status: 'graded', feedback: target.feedback ?? '' }}
      mutation={(v) => productApi.gradeHomework(institution, homeworkId, {
        student_id: target.student_id,
        grade_out_of_10: v.grade,
        status: v.status,
        feedback: v.feedback?.trim() || undefined,
      })}
      onSuccess={onDone}
    >
      {target.note && (
        <Alert type="info" showIcon message="Student's submission note" description={target.note} style={{ marginBottom: 10 }} />
      )}
      <FormSection cols={2}>
        <Field name="grade" label="Grade (out of 10)" required>
          <InputNumber min={0} max={10} style={{ width: '100%' }} />
        </Field>
        <Field name="status" label="Status" required>
          <Select options={[
            { value: 'graded', label: 'Graded' },
            { value: 'returned', label: 'Returned (needs redo)' },
            { value: 'missing', label: 'Missing' },
          ]} />
        </Field>
      </FormSection>
      <Field name="feedback" label="Feedback">
        <Input.TextArea rows={3} placeholder="Note back to the student" />
      </Field>
    </FormModal>
  );
}
